/**
 * hast.ts — the lossless HTML AST that is the single source of truth for the
 * static document renderer.
 *
 * CONTRACT (isomorphism): `toHtml(parse(x))` produces a *canonical* form of `x`.
 * Byte-equality with arbitrary input is NOT a goal (the HTML parser normalises
 * quoting, entities, void elements, and inserts implied tags like <tbody>).
 * The guarantee we hold and test is **idempotent canonical equality**:
 *
 *     const c = toHtml(parse(x));
 *     toHtml(parse(c)) === c;            // idempotent
 *
 * Practical use: canonicalise once at import (store `toHtml(parse(raw))`), and
 * thereafter `toHtml(parse(stored)) === stored` holds exactly.
 *
 * This module has no runtime dependencies — it uses the DOM (`<template>`),
 * available in the browser and in jsdom under Jest.
 */

export interface ElementNode {
    type: "element";
    tagName: string;
    /** All attributes, verbatim, in source order. Lossless. */
    properties: Record<string, string>;
    children: HastNode[];
}

export interface TextNode {
    type: "text";
    value: string;
}

export interface CommentNode {
    type: "comment";
    value: string;
}

export type HastNode = ElementNode | TextNode | CommentNode;

// HTML void elements: serialised without a closing tag and never given children.
const VOID_ELEMENTS = new Set([
    "area", "base", "br", "col", "embed", "hr", "img", "input",
    "link", "meta", "param", "source", "track", "wbr",
]);

/** Parse an HTML fragment into a lossless hast node list. */
export function parse(html: string): HastNode[] {
    const tpl = document.createElement("template");
    tpl.innerHTML = html;
    return childrenToHast(tpl.content);
}

function childrenToHast(parent: Node): HastNode[] {
    const out: HastNode[] = [];
    parent.childNodes.forEach((node) => {
        const mapped = fromDom(node);
        if (mapped) out.push(mapped);
    });
    return out;
}

function fromDom(node: Node): HastNode | null {
    switch (node.nodeType) {
        case Node.ELEMENT_NODE: {
            const el = node as Element;
            const properties: Record<string, string> = {};
            for (const attr of Array.from(el.attributes)) {
                properties[attr.name] = attr.value;
            }
            return {
                type: "element",
                tagName: el.tagName.toLowerCase(),
                properties,
                children: childrenToHast(el),
            };
        }
        case Node.TEXT_NODE:
            return { type: "text", value: (node as Text).data };
        case Node.COMMENT_NODE:
            return { type: "comment", value: (node as Comment).data };
        default:
            // CDATA, processing instructions, etc. — not expected in HTML fragments.
            return null;
    }
}

/** Serialise a hast node list back to an HTML string (the canonical form). */
export function toHtml(nodes: HastNode[]): string {
    return nodes.map(serialize).join("");
}

function serialize(node: HastNode): string {
    switch (node.type) {
        case "text":
            return escapeText(node.value);
        case "comment":
            return `<!--${node.value}-->`;
        case "element": {
            const attrs = Object.entries(node.properties)
                .map(([name, value]) => ` ${name}="${escapeAttr(value)}"`)
                .join("");
            if (VOID_ELEMENTS.has(node.tagName)) {
                return `<${node.tagName}${attrs}>`;
            }
            return `<${node.tagName}${attrs}>${toHtml(node.children)}</${node.tagName}>`;
        }
    }
}

function escapeText(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escapeAttr(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

/** Convenience: produce the canonical form of an HTML fragment. */
export function canonicalize(html: string): string {
    return toHtml(parse(html));
}

/** All visible text under a node, concatenated (comments excluded). */
export function nodeText(node: HastNode): string {
    switch (node.type) {
        case "text":
            return node.value;
        case "comment":
            return "";
        case "element":
            return node.children.map(nodeText).join("");
    }
}
